import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, ClipboardCheck, ChevronDown } from "lucide-react";
import { CALENDLY_URL } from "../lib/site";
import Tm from "./ow/Tm";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", hasMenu: true },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
  { to: "/careers", label: "Careers" },
];

const SERVICES_MENU = [
  { to: "/services#fractional", label: "Fractional Executive Leadership" },
  { to: "/services#operations", label: "Operational Excellence" },
  { to: "/services#people", label: "People Operations & Recruiting" },
  { to: "/services/operator-walk", label: "The Operator Walk", tm: true },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-ink-line"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-logo-link"
            onClick={() => setOpen(false)}
          >
            <div className="w-9 h-9 bg-brand-700 text-white flex items-center justify-center font-serif text-lg leading-none">
              H
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-brand-700">Holder Hospitality</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-ink-muted -mt-0.5">
                Consulting
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV.map((item) =>
              item.hasMenu ? (
                <div key={item.to} className="relative group" data-testid="nav-services-dropdown">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-sm tracking-wide transition-colors ${
                        isActive ? "text-brand-700 font-medium" : "text-ink-soft hover:text-brand-700"
                      }`
                    }
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                  </NavLink>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-150">
                    <div
                      className="w-80 bg-white border border-ink-line shadow-xl py-2"
                      data-testid="nav-services-menu"
                    >
                      {SERVICES_MENU.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="block px-5 py-3 text-sm text-ink-soft hover:bg-brand-100 hover:text-brand-700 transition-colors"
                          data-testid={`nav-service-link-${s.to.split(/[#/]/).pop()}`}
                        >
                          {s.label}
                          {s.tm && <Tm />}
                        </Link>
                      ))}
                      <div className="border-t border-ink-line mt-2 pt-2">
                        <Link
                          to="/services"
                          className="block px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-brand-500 hover:text-brand-700 transition-colors"
                          data-testid="nav-service-link-all"
                        >
                          View all services
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors ${
                      isActive ? "text-brand-700 font-medium" : "text-ink-soft hover:text-brand-700"
                    }`
                  }
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
            <NavLink
              to="/assessment"
              className={({ isActive }) =>
                `relative inline-flex items-center gap-2 px-3 py-1.5 text-sm tracking-wide border transition-colors ${
                  isActive
                    ? "bg-brand-700 text-white border-brand-700"
                    : "bg-brand-100 text-brand-700 border-brand-200 hover:bg-brand-700 hover:text-white hover:border-brand-700"
                }`
              }
              data-testid="nav-link-assessment"
            >
              <ClipboardCheck className="w-3.5 h-3.5" />
              Free Assessment
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center bg-brand-400 text-white text-[8px] font-semibold tracking-widest uppercase px-1.5 py-0.5 leading-none"
                aria-hidden
              >
                New
              </span>
            </NavLink>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-medium px-5 py-3 transition-colors group"
              data-testid="nav-book-call-btn"
            >
              Book a Call
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-brand-700"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-ink-line bg-white" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-3 text-base block ${
                      isActive ? "text-brand-700 font-medium" : "text-ink-soft"
                    }`
                  }
                  data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
                {item.hasMenu && (
                  <div className="ml-4 pl-3 border-l border-ink-line flex flex-col" data-testid="mobile-services-submenu">
                    {SERVICES_MENU.map((s) => (
                      <Link
                        key={s.to}
                        to={s.to}
                        onClick={() => setOpen(false)}
                        className="py-2.5 text-sm text-ink-soft hover:text-brand-700 transition-colors"
                        data-testid={`mobile-nav-service-link-${s.to.split(/[#/]/).pop()}`}
                      >
                        {s.label}
                        {s.tm && <Tm />}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <NavLink
              to="/assessment"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `mt-3 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm border ${
                  isActive
                    ? "bg-brand-700 text-white border-brand-700"
                    : "bg-brand-100 text-brand-700 border-brand-200"
                }`
              }
              data-testid="mobile-nav-link-assessment"
            >
              <ClipboardCheck className="w-4 h-4" /> Free Assessment
            </NavLink>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-brand-700 text-white text-sm font-medium px-5 py-3"
              data-testid="mobile-book-call-btn"
            >
              Book a Call <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
